import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Info, AlertOctagon, type LucideIcon } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Auto-dismiss delay in ms. Pass 0 to keep it until manually dismissed. Default: 5000 */
  duration?: number;
  /** Optional action button */
  action?: { label: string; onClick: () => void };
}

/**
 * What components do with toasts. It is one object for the provider's whole life (the list
 * of toasts on screen is not part of it), so showing or dismissing a toast does not render
 * the components that only raise them.
 */
interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  /** Convenience helpers */
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>');
  return ctx;
}

// ── Variant styling ──────────────────────────────────────────────────────────

// Every toast is the same glass; what kind it is shows in its tone (data-tone on the toast:
// the icon's chip and the progress line take their colours from it).
const VARIANT_CONFIG: Record<ToastVariant, { icon: LucideIcon; tone: 'ok' | 'danger' | 'warn' | 'lavender' }> = {
  success: { icon: CheckCircle,   tone: 'ok' },
  error:   { icon: AlertOctagon,  tone: 'danger' },
  warning: { icon: AlertTriangle, tone: 'warn' },
  info:    { icon: Info,          tone: 'lavender' },
};

const DEFAULT_DURATION = 5000;
const MAX_VISIBLE = 5;

// ── Single toast item ────────────────────────────────────────────────────────

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const { icon: Icon, tone } = VARIANT_CONFIG[toast.variant];
  const duration = toast.duration ?? DEFAULT_DURATION;
  const [exiting, setExiting] = useState(false);
  const [remaining, setRemaining] = useState(duration);
  const startTimeRef = useRef(Date.now());
  const pausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 250); // match exit animation
  }, [onRemove, toast.id]);

  // Auto-dismiss timer
  useEffect(() => {
    if (duration <= 0) return;

    const startTimer = () => {
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(dismiss, remaining);
    };

    startTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [duration, dismiss]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = () => {
    if (duration <= 0) return;
    pausedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    const elapsed = Date.now() - startTimeRef.current;
    setRemaining(prev => Math.max(0, prev - elapsed));
  };

  const handleMouseLeave = () => {
    if (duration <= 0) return;
    pausedRef.current = false;
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remaining);
  };

  return (
    <div
      role="alert"
      data-tone={tone}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        ns-glass ns-glass-strong
        relative flex items-start gap-3 w-[380px] max-w-[calc(100vw-2rem)] p-4 pr-3 rounded-2xl
        ${exiting ? 'animate-toastExit' : 'animate-toastEnter'}
        overflow-hidden pointer-events-auto
      `}
    >
      {/* Icon, in the round mark of a row, in the toast's tone */}
      <span className="ns-mark ns-mark-sm">
        <Icon />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1.5">
        <p className="ns-t-title">{toast.title}</p>
        {toast.description && (
          <p className="ns-t-small mt-1">{toast.description}</p>
        )}
        {toast.action && (
          <button
            onClick={() => { toast.action!.onClick(); dismiss(); }}
            className="ns-link mt-2 text-xs"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={dismiss}
        className="ns-icon-btn ns-icon-btn-sm"
        aria-label="Dismiss"
      >
        <X />
      </button>

      {/* Auto-dismiss progress bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cream-200 overflow-hidden">
          <div
            className="h-full bg-[color:var(--tone)] origin-left"
            style={{
              animation: `shrinkWidth ${duration}ms linear forwards`,
              animationPlayState: pausedRef.current ? 'paused' : 'running',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Provider + container ─────────────────────────────────────────────────────

let toastIdCounter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Keep a stable ref so we don't force re-renders when consumers just call addToast
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${++toastIdCounter}-${Date.now()}`;
    setToasts(prev => {
      const next = [...prev, { ...toast, id }];
      // Evict oldest if over limit
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    return id;
  }, []);

  const clearToasts = useCallback(() => setToasts([]), []);

  const success = useCallback(
    (title: string, description?: string) => addToast({ variant: 'success', title, description }),
    [addToast],
  );
  const error = useCallback(
    (title: string, description?: string) => addToast({ variant: 'error', title, description }),
    [addToast],
  );
  const warning = useCallback(
    (title: string, description?: string) => addToast({ variant: 'warning', title, description }),
    [addToast],
  );
  const info = useCallback(
    (title: string, description?: string) => addToast({ variant: 'info', title, description }),
    [addToast],
  );

  // Every member is stable, so this is the same object for as long as the provider lives
  const value = useMemo<ToastContextValue>(() => ({
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
  }), [addToast, removeToast, clearToasts, success, error, warning, info]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast container — fixed bottom-right, above all modals */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 right-4 z-[9999] flex flex-col-reverse gap-2 pointer-events-none"
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>

      {/* Inject keyframe animations */}
      <style>{`
        @keyframes toastEnter {
          from { opacity: 0; transform: translateX(100%) scale(0.95); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastExit {
          from { opacity: 1; transform: translateX(0) scale(1); }
          to   { opacity: 0; transform: translateX(100%) scale(0.95); }
        }
        @keyframes shrinkWidth {
          from { width: 100%; }
          to   { width: 0%; }
        }
        .animate-toastEnter {
          animation: toastEnter 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
        }
        .animate-toastExit {
          animation: toastExit 0.25s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
        }
      `}</style>
    </ToastContext.Provider>
  );
}
